import { useEffect, useRef, useState } from 'react';
import { useButton } from 'react-aria';
import styled, { css } from 'styled-components';
import { getFocusRingStyles } from 'visitor-ui-component-library/utils/getFocusRingStyles';
import LauncherContent, { MAX_TEXTAREA_HEIGHT } from './LauncherContent';
import { calculateSpotlightLauncherWidth } from '../widget-dimensions/calculateSpotlightLauncherWidth';
import { jsx as _jsx } from "react/jsx-runtime";
export { IconButton } from './LauncherContent';
const TRANSITION_EASING = 'cubic-bezier(0.4, 0, 0.2, 1)';
const Container = styled.div.withConfig({
  displayName: "SpotlightLauncherShell__Container"
})(["position:relative;border:1px solid #ccc;border-radius:24px;padding:10px 8px;display:flex;flex-direction:column;gap:8px;min-height:48px;width:", "px;background-color:white;cursor:", ";box-sizing:border-box;margin-bottom:32px;transition:", ";outline:none;", ""], ({
  $width
}) => $width, ({
  $disabled,
  $isButton
}) => $isButton ? 'pointer' : $disabled ? 'not-allowed' : 'text', ({
  $shouldAnimate
}) => $shouldAnimate ? `width 300ms ${TRANSITION_EASING}, background-color 150ms ease` : 'background-color 300ms ease', ({
  $isButton
}) => $isButton && css(["&:hover{background-color:#f5f5f5;}", ""], getFocusRingStyles({
  outlineOffset: '3px'
})));
const SpotlightLauncherShell = ({
  placeholder,
  sendAriaLabel,
  onSubmit,
  onOpen,
  open = false,
  disabled = false,
  disableSubmit = false,
  hasAttachments = false,
  expanded = false,
  shouldAnimate = true,
  isButton = false,
  buttonLabel,
  addMenu,
  fileUploads,
  browserWindowWidth,
  onExpandedChange
}) => {
  const inputRef = useRef(null);
  const wrapperRef = useRef(null);
  const containerRef = useRef(null);
  const {
    buttonProps
  } = useButton({
    onPress: onOpen,
    elementType: 'div',
    'aria-label': buttonLabel
  }, containerRef);
  const [messageText, setMessageText] = useState('');
  const [isExpanded, setIsExpanded] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);
  useEffect(() => {
    setHasLoaded(true);
  }, []);
  const canSubmit = !disabled && !disableSubmit;
  const hasTextContent = Boolean(messageText.trim());
  const isExpansionActive = open || isExpanded || expanded;
  const width = calculateSpotlightLauncherWidth(browserWindowWidth, isExpansionActive);
  const handleChange = e => {
    setMessageText(e.target.value);
    if (wrapperRef.current) {
      wrapperRef.current.dataset.value = e.target.value;
    }
    e.target.style.overflowY = e.target.scrollHeight >= MAX_TEXTAREA_HEIGHT ? 'auto' : 'hidden';
  };
  const handleSubmit = () => {
    const hasContent = hasTextContent || hasAttachments;
    if (!hasContent && !disabled) {
      onOpen();
      return;
    }
    if (!canSubmit || !hasContent) return;
    onSubmit(messageText);
    setMessageText('');
    setIsExpanded(false);
    onExpandedChange === null || onExpandedChange === void 0 || onExpandedChange(false);
    if (wrapperRef.current) {
      wrapperRef.current.dataset.value = '';
    }
    if (inputRef.current) {
      inputRef.current.style.overflowY = 'hidden';
    }
  };
  const handleKeyDown = e => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };
  const handleFocus = () => {
    setIsExpanded(true);
    onExpandedChange === null || onExpandedChange === void 0 || onExpandedChange(true);
  };
  const handleBlur = () => {
    const next = hasTextContent;
    setIsExpanded(next);
    onExpandedChange === null || onExpandedChange === void 0 || onExpandedChange(next);
  };
  const handleContainerClick = () => {
    if (isButton) {
      onOpen();
      return;
    }
    if (!disabled) {
      var _inputRef$current;
      (_inputRef$current = inputRef.current) === null || _inputRef$current === void 0 || _inputRef$current.focus();
    }
  };
  return /*#__PURE__*/_jsx(Container, Object.assign({}, isButton ? buttonProps : {}, {
    ref: isButton ? containerRef : undefined,
    $width: width,
    $shouldAnimate: shouldAnimate && hasLoaded,
    $disabled: disabled,
    $isButton: isButton,
    "data-test-id": "spotlight-launcher",
    onClick: handleContainerClick,
    children: /*#__PURE__*/_jsx(LauncherContent, {
      isButton: isButton,
      buttonLabel: buttonLabel,
      addMenu: addMenu,
      fileUploads: fileUploads,
      wrapperRef: wrapperRef,
      inputRef: inputRef,
      placeholder: placeholder,
      messageText: messageText,
      onKeyDown: handleKeyDown,
      onChange: handleChange,
      onFocus: handleFocus,
      onBlur: handleBlur,
      disabled: disabled,
      canSubmit: canSubmit,
      hasTextContent: hasTextContent,
      hasAttachments: hasAttachments,
      onSubmit: handleSubmit,
      sendAriaLabel: sendAriaLabel
    })
  }));
};
SpotlightLauncherShell.displayName = 'SpotlightLauncherShell';
export default SpotlightLauncherShell;